// Stockage du brouillon wizard dans IndexedDB (quota 50MB à 1GB selon navigateur)
// — remplace le localStorage (5MB) qui ne pouvait pas contenir les photos base64.
// Fallback silencieux : si IndexedDB échoue, on tombe sur localStorage sans les médias.
//
// lot BT — 🔴 la clé était FIXE (`current_draft`) : IndexedDB n'étant isolé ni par compte ni
// par onglet, le compte suivant ouvert dans le même navigateur se voyait proposer la saisie
// d'un autre utilisateur — et pouvait créer ses données (propriétaire, prix, photos) dans la
// mauvaise agence. La clé porte désormais l'utilisateur ET l'agence, et le payload les répète
// pour un second contrôle à la lecture.

import { getCurrentUser } from '../../utils/auth';

const DB_NAME = 'komerces_wizard_db';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';
const LEGACY_DRAFT_KEY = 'current_draft';
const LEGACY_LOCALSTORAGE_KEY = 'komerces_wizard_draft_v1';

const proprietaire = () => {
  const user = getCurrentUser();
  if (!user?.id) return null;
  return { userId: String(user.id), agenceId: String(user.agence_id || 'sans_agence') };
};

const cles = () => {
  const qui = proprietaire();
  if (!qui) return null;
  return {
    ...qui,
    idb: `draft_${qui.userId}_${qui.agenceId}`,
    ls: `komerces_wizard_draft_v2_${qui.userId}_${qui.agenceId}`,
  };
};

const openDB = () =>
  new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });

// Les brouillons d'avant le lot BT n'ont pas de propriétaire connu : on les supprime sans
// jamais les proposer. Une seule fois par chargement de l'application.
let heritageTraite = false;
const purgerAncienneCle = async () => {
  if (heritageTraite) return;
  heritageTraite = true;
  try {
    const db = await openDB();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(LEGACY_DRAFT_KEY);
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    });
  } catch {
    // no-op
  }
  try {
    localStorage.removeItem(LEGACY_LOCALSTORAGE_KEY);
  } catch {
    // no-op
  }
};

export const saveWizardDraft = async (payload) => {
  const k = cles();
  if (!k) return false; // ⛔ pas de brouillon anonyme
  const marque = { ...payload, userId: k.userId, agenceId: k.agenceId };
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(marque, k.idb);
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    // Fallback : localStorage sans les médias (mieux que rien)
    try {
      const light = { ...marque };
      if (light.data) {
        const { photos, documents, ...rest } = light.data;
        light.data = rest;
      }
      localStorage.setItem(k.ls, JSON.stringify(light));
      return true;
    } catch {
      console.warn('[wizardDraftStorage] save failed:', e);
      return false;
    }
  }
};

// Second contrôle : la clé pourrait avoir été forgée à la main (DevTools).
const appartient = (draft, k) =>
  !!draft && String(draft.userId) === k.userId && String(draft.agenceId) === k.agenceId;

export const loadWizardDraft = async () => {
  await purgerAncienneCle();
  const k = cles();
  if (!k) return null;
  try {
    const db = await openDB();
    const draft = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(k.idb);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    return appartient(draft, k) ? draft : null;
  } catch {
    // Fallback localStorage (si IndexedDB indisponible)
    try {
      const raw = localStorage.getItem(k.ls);
      const draft = raw ? JSON.parse(raw) : null;
      return appartient(draft, k) ? draft : null;
    } catch {
      return null;
    }
  }
};

export const clearWizardDraft = async () => {
  const k = cles();
  if (!k) return; // ⛔ ne jamais toucher aux brouillons des autres comptes
  try {
    const db = await openDB();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(k.idb);
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    });
  } catch {
    // no-op
  }
  try {
    localStorage.removeItem(k.ls);
  } catch {
    // no-op
  }
};
